import { Add, Delete, Edit } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { SetStateAction, useState } from 'react';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import {
  useGetRelationshipRoles,
  useGetRelationshipTypes,
} from '../../hooks/reactQuerys/RelationshipQueries';
import type { ILegalClient } from '../../Interfaces/ILegalClient';
import { type ILegalClientRelationship } from '../../Interfaces/ILegalClientRelationship/ILegalClientRelationship';
import { UpdateOrCreateRelationshipDialog } from './UpdateOrCreateRelationshipDialog';

interface IProps {
  editedLegalClientId?: number;
  relationships: ILegalClientRelationship[];
  setRelationships: React.Dispatch<SetStateAction<ILegalClientRelationship[]>>;
}

export const RelationshipsTab = ({
  editedLegalClientId,
  relationships,
  setRelationships,
}: IProps) => {
  const [isMount, setIsMount] = useState(false);
  const [selectedRelationship, setSelectedRelationship] =
    useState<ILegalClientRelationship>();

  const { relationshipTypes, isLoadingRelationshipTypes } =
    useGetRelationshipTypes();
  const { relationshipRoles, isLoadingRelationshipRoles } =
    useGetRelationshipRoles();

  if (
    isLoadingRelationshipTypes ||
    isLoadingRelationshipRoles ||
    !(relationshipTypes && relationshipRoles)
  ) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h5'>Beziehungen</Typography>
        <Tooltip title='Beziehung hinzufügen'>
          <IconButton onClick={() => setIsMount(true)}>
            <Add />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Beziehung</TableCell>
              <TableCell>Rolle</TableCell>
              <TableCell>Person</TableCell>
              <TableCell width={110}>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {relationships.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <i>Keine Beziehungen vorhanden.</i>
                </TableCell>
              </TableRow>
            ) : (
              relationships.map(r =>
                r.Members.map((m, i) => (
                  <TableRow key={i}>
                    {i === 0 && (
                      <TableCell rowSpan={r.Members.length}>
                        {
                          relationshipTypes.find(
                            x => x.idRelationshipType === r.idRelationshipType
                          )?.RelationshipType
                        }
                      </TableCell>
                    )}
                    <TableCell>
                      {
                        relationshipRoles.find(
                          x => x.idRelationshipRole === m.idRelationshipRole
                        )?.RelationshipRole
                      }
                    </TableCell>
                    <TableCell>
                      {m.LegalClient.FirstName} {m.LegalClient.LastName}
                    </TableCell>
                    {i === 0 && (
                      <TableCell rowSpan={r.Members.length}>
                        <IconButton
                          size='small'
                          onClick={() => {
                            setSelectedRelationship(r);
                            setIsMount(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size='small'
                          onClick={() =>
                            setRelationships([
                              ...relationships.filter(
                                x => x.idRelationship !== r.idRelationship
                              ),
                            ])
                          }
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )
            )}
          </TableBody>
        </Table>
      </Box>
      {isMount && (
        <UpdateOrCreateRelationshipDialog
          legalClients={[]}
          editedLegalClientId={editedLegalClientId}
          selectedRelationship={selectedRelationship}
          relationships={relationships}
          setRelationships={setRelationships}
          unmount={() => {
            setIsMount(false);
            setSelectedRelationship(undefined);
          }}
        />
      )}
    </Box>
  );
};
